import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Calendar, User, Eye } from 'lucide-react';
import ApiService from '@/services/api.service';
import { BuildAllModal } from '@/components/BuildAllModal';

const PAGE_SIZE = 10;

type BuildOrchestrationHistoryRow = {
  id: string;
  job_id?: string;
  status: string;
  queued_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  executed_by?: string | null;
  node_selector?: string | null;
  error?: string | null;
  scheduled_build?: boolean;
};

type BuildHistoryResponse = {
  records: BuildOrchestrationHistoryRow[];
  total_records: number;
  total_pages: number;
  page: number;
  limit: number;
};

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'info' | 'secondary' | 'warning' | 'outline'> = {
  COMPLETE: 'success',
  ERROR: 'destructive',
  RUNNING: 'info',
  QUEUED: 'secondary',
  CANCELLED: 'warning',
};

const statusVariant = (status: string) => STATUS_VARIANT[status?.toUpperCase()] ?? 'outline';

const formatRelative = (iso?: string | null): string => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const formatDuration = (start?: string | null, end?: string | null): string => {
  if (!start || !end) return '';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec ? `${min}m ${remSec}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr}h ${remMin}m` : `${hr}h`;
};

export default function BuildHistoryCard() {
  const [page, setPage] = useState(1);
  const [detailOrchestrationId, setDetailOrchestrationId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<BuildHistoryResponse>({
    queryKey: ['build-history', page, PAGE_SIZE],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/buildorchestrations/history?page=${page}&limit=${PAGE_SIZE}`,
      );
      return result as BuildHistoryResponse;
    },
    staleTime: 15 * 1000,
    placeholderData: keepPreviousData,
  });

  const totalPages = data?.total_pages ?? 1;
  const totalRecords = data?.total_records ?? 0;
  const rows = data?.records ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Build History</CardTitle>
        <CardDescription>
          Recent scheduled and manual builds. Useful for diagnosing failures and confirming that scheduled runs completed.
          {totalRecords > 0 && (
            <span className="ml-1 text-muted-foreground">
              ({totalRecords} total)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading build history…
          </div>
        ) : isError ? (
          <div className="py-6 text-center text-sm text-destructive">
            Failed to load build history.
          </div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No builds yet.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="w-[1%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((b) => {
                    const duration = formatDuration(b.started_at ?? b.queued_at, b.completed_at);
                    return (
                      <TableRow key={b.id}>
                          <TableCell>
                            <Badge variant={statusVariant(b.status)}>
                              {b.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {b.scheduled_build ? (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                Scheduled
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <User className="h-3 w-3" />
                                Manual
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            <span title={b.queued_at ?? undefined}>
                              {formatRelative(b.queued_at)}
                            </span>
                            {duration && (
                              <div className="text-xs text-muted-foreground">{duration}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{b.executed_by ?? '—'}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setDetailOrchestrationId(b.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
      <BuildAllModal
        open={!!detailOrchestrationId}
        onOpenChange={(open) => { if (!open) setDetailOrchestrationId(null); }}
        orchestrationId={detailOrchestrationId ?? undefined}
      />
    </Card>
  );
}
